import { PermissionGuard } from '@/components/PermissionGuard';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { fadeUp } from '@/lib/animations';
import { defaultColor, operatorColors, operatorImages } from '@/lib/constants';
import { statusMapping } from '@/lib/statusMapping';
import type { MobileLine } from '@octave/contracts';
import { motion } from 'framer-motion';
import {
  ArrowRightLeft,
  Ban,
  ChevronDown,
  Globe,
  Mail,
  Pause,
  Pencil,
  Play,
  Settings2,
  User,
} from 'lucide-react';
import {
  getStatusStyle,
  isLineActionsDisabled,
  isToggleSuspendDisabled,
} from './helpers';

interface LineHeroCardProps {
  lineDetails: MobileLine;
  isActionPending: boolean;
  onUpdateUser: () => void;
  onChangePlan: () => void;
  onToggleSuspend: () => void;
  onTerminate: () => void;
  onOrderTravelEsim: () => void;
}

/** Hero card: number + status, identity strip, actions menu and key info. */
export function LineHeroCard({
  lineDetails,
  isActionPending,
  onUpdateUser,
  onChangePlan,
  onToggleSuspend,
  onTerminate,
  onOrderTravelEsim,
}: LineHeroCardProps) {
  const statusStyle = getStatusStyle(lineDetails.status);

  return (
    <motion.div
      variants={fadeUp}
      className="rounded-2xl bg-white p-4 sm:p-8 shadow-sm"
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-4">
            <h1 className="text-2xl sm:text-4xl font-bold tabular-nums tracking-tight text-slate-900">
              {lineDetails.number}
            </h1>
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${statusStyle.bg}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${statusStyle.dot}`} />
              {statusMapping[lineDetails.status ?? 'unknown'] ||
                lineDetails.status}
            </span>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-400">
            {lineDetails.identity && (
              <span className="flex items-center gap-1.5">
                <User className="h-3.5 w-3.5" />
                {lineDetails.identity.firstName} {lineDetails.identity.lastName}
              </span>
            )}
            {lineDetails.identity?.email && (
              <span className="flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" />
                {lineDetails.identity.email}
              </span>
            )}
          </div>
        </div>

        <PermissionGuard permission="write">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="group flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 bg-white py-2 pl-3 pr-2.5 text-sm font-medium text-slate-700 shadow-sm transition-all hover:border-slate-300 hover:shadow data-[state=open]:border-slate-300 data-[state=open]:bg-slate-50">
                <Settings2 className="h-4 w-4 text-slate-400 transition-colors group-hover:text-sky-600 group-data-[state=open]:text-sky-600" />
                Actions
                <ChevronDown className="h-4 w-4 text-slate-400 transition-transform duration-200 group-data-[state=open]:rotate-180" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem
                onClick={onUpdateUser}
                disabled={isLineActionsDisabled(lineDetails.status)}
                className="cursor-pointer"
              >
                <Pencil className="mr-2 h-4 w-4" />
                Modifier l'utilisateur
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={onChangePlan}
                disabled={isLineActionsDisabled(lineDetails.status)}
                className="cursor-pointer"
              >
                <ArrowRightLeft className="mr-2 h-4 w-4" />
                Changer de forfait
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={onOrderTravelEsim}
                disabled={isLineActionsDisabled(lineDetails.status)}
                className="cursor-pointer"
              >
                <Globe className="mr-2 h-4 w-4" />
                Commander une eSIM internationale
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={onToggleSuspend}
                disabled={
                  isToggleSuspendDisabled(lineDetails.status) || isActionPending
                }
                className="cursor-pointer"
              >
                {lineDetails.status === 'suspended' ? (
                  <Play className="mr-2 h-4 w-4" />
                ) : (
                  <Pause className="mr-2 h-4 w-4" />
                )}
                {lineDetails.status === 'suspended' ? 'Réactiver' : 'Suspendre'}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={onTerminate}
                disabled={
                  isLineActionsDisabled(lineDetails.status) || isActionPending
                }
                className="cursor-pointer text-red-600 focus:text-red-600"
              >
                <Ban className="mr-2 h-4 w-4" />
                Résilier
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </PermissionGuard>
      </div>

      {/* Key info strip */}
      <div className="mt-4 sm:mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-xl bg-slate-50 px-4 py-3">
          <p className="text-xs text-slate-400">Forfait</p>
          <p className="mt-0.5 text-sm font-semibold text-slate-900">
            {lineDetails.phonePlan.name}
          </p>
        </div>
        <div className="rounded-xl bg-slate-50 px-4 py-3">
          <p className="text-xs text-slate-400">Prix du forfait</p>
          <p className="mt-0.5 text-sm font-semibold text-slate-900">
            {(lineDetails.phonePlan.price / 100).toFixed(2)} €/mois
          </p>
        </div>
        <div className="rounded-xl bg-slate-50 px-4 py-3">
          <p className="text-xs text-slate-400">Opérateur</p>
          <div className="mt-0.5 flex items-center gap-2">
            {operatorImages[lineDetails.phonePlan.operator.name] ? (
              <img
                src={operatorImages[lineDetails.phonePlan.operator.name]}
                alt={lineDetails.phonePlan.operator.name}
                className="h-5 w-5 object-contain"
              />
            ) : (
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{
                  backgroundColor:
                    operatorColors[lineDetails.phonePlan.operator.name] ||
                    defaultColor,
                }}
              />
            )}
            <p className="text-sm font-semibold text-slate-900">
              {lineDetails.phonePlan.operator.name}
            </p>
          </div>
        </div>
        <div className="rounded-xl bg-slate-50 px-4 py-3">
          <p className="text-xs text-slate-400">Enveloppe data</p>
          <p className="mt-0.5 text-sm font-semibold text-slate-900">
            {lineDetails.phonePlan.dataLimitMo > 0
              ? `${lineDetails.phonePlan.dataLimitMo / 1000} Go`
              : 'Illimité'}
          </p>
        </div>
      </div>
    </motion.div>
  );
}
